import './HallPointRewards.scss';

import { Badge, Button, Card, Col, Form, Row } from 'react-bootstrap';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import AppCard from '../../../commons/cards/AppCard';
import AppInput from '../../../commons/inputs/AppInput';
import AppLoading from '../../../commons/AppLoading';
import FloatingVideoButton from '../../../commons/buttons/FloatingVideoButton';
import HallAppToast from '../../../commons/HallAppToast';
import HallPointsAPI from '../HallPointsAPI';
import MenuTitle from '../../../commons/menu/MenuTitle';
import ModalHallPointRewardsConfig from './ModalHallPointRewardsConfig';
import { normalizeString } from '../../../commons/util/HallAppUtil';

const hallPointsAPI = new HallPointsAPI();

function HallPointRewards() {
  // Removido: points não é usado para render; mantive o setPoints se precisar no futuro (ex.: validação/salvar em massa)
  // const [points, setPoints] = useState({});
  const [services, setServices] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showModalConfig, setShowModalConfig] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  const searchRef = useRef(null);
  const debounceTimer = useRef(null);

  const dayMap = useMemo(() => ({
    MONDAY: 'Segunda',
    TUESDAY: 'Terça',
    WEDNESDAY: 'Quarta',
    THURSDAY: 'Quinta',
    FRIDAY: 'Sexta',
    SATURDAY: 'Sábado',
    SUNDAY: 'Domingo',
  }), []);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await hallPointsAPI.getRewards();
      const data = Array.isArray(response?.data) ? response.data : [];

      // Ordena pelo nome do serviço
      data.sort((a, b) => (a.serviceName || '').localeCompare(b.serviceName || ''));

      setServices(data);
      setFiltered(data);
    } catch (e) {
      HallAppToast.error('Não foi possível carregar as recompensas.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = useCallback(async (config) => {
    try {
      setIsLoading(true);
      await hallPointsAPI.deleteRewards(config.id);
      HallAppToast.success('Configuração removida com sucesso.');
      await load(); // recarrega a lista
    } catch (e) {
      HallAppToast.error('Falha ao remover a configuração.');
      setIsLoading(false);
    }
  }, [load]);

  // Busca com debounce
  const onSearch = (value) => {
    setQuery(value);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      const q = normalizeString(value || '');
      if (!q) {
        setFiltered(services);
        return;
      }
      const results = services.filter((s) =>
        normalizeString(s?.serviceName || '').includes(q)
      );
      setFiltered(results);
    }, 250);
  };

  const total = filtered.length;

  return (
    <div className="hallpoint-rewards pb-5">
      <div className="container">
        <MenuTitle title="Configuração de Recompensas" />

        <p className="text-muted mb-3">
          Defina quantos HallPoints são necessários para que seus clientes resgatem cada serviço como recompensa.
          Clique em um serviço para configurar.
        </p>

        <Form className="mb-3">
          <Row className="g-3 align-items-end">
            <Col xs={12} md={8} lg={6}>
              <AppInput
                label="Pesquisar serviço"
                name="search"
                placeholder="Digite o nome do serviço"
                errors={[]}
                inputRef={searchRef}
                value={query}
                onChange={(e) => onSearch(e.target.value)}
              />
            </Col>
            <Col xs="auto" className="ms-auto">
              <div className="text-muted small mt-2">
                {isLoading ? 'Carregando...' : `${total} resultado(s)`}
              </div>
            </Col>
          </Row>
        </Form>

        <hr className="my-3" />

        {isLoading ? (
          <AppLoading />
        ) : total === 0 ? (
          <div className="text-center text-muted py-5">
            <div className="mb-2 fw-semibold">Nenhum serviço encontrado</div>
            <div className="small">
              Tente ajustar sua busca ou limpar o filtro para ver todos os serviços.
            </div>
          </div>
        ) : (
          <Form>
            <Row className="g-3">
              {filtered.map((service) => (
                <Col key={service.serviceId} xs={12} md={6} lg={4}>
                  <AppCard
                    className="p-3 h-100 service-card"
                    onClick={() => {
                      setSelectedService(service);
                      setShowModalConfig(true);
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      {/* Info do Serviço */}
                      <div className="pe-2">
                        <Card.Title className="mb-1 fw-bold text-dark">
                          {service.serviceName}
                        </Card.Title>
                        <small className="text-muted d-block">Disponível em:</small>
                        <div className="mt-1 d-flex flex-wrap">
                          {(service?.weekDays || []).map((day) => (
                            <Badge
                              key={day}
                              bg="light"
                              text="dark"
                              className="me-1 mb-1 border"
                            >
                              {dayMap[day] || day}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Pontuação configurada */}
                      {service.qtdPoints ? (
                        <Badge className="fs-6 custom-badge" bg="primary">
                          {service.qtdPoints} pts
                        </Badge>
                      ) : (
                        <Badge className="fs-6 custom-badge" bg="secondary">
                          Sem configuração
                        </Badge>
                      )}
                    </div>

                    {/* Ações extras se configurado */}
                    {service.id && (
                      <>
                        <hr />
                        <div className="text-end">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation(); // evita abrir o modal
                              remove(service);
                            }}
                          >
                            Remover Configuração
                          </Button>
                        </div>
                      </>
                    )}
                  </AppCard>
                </Col>
              ))}
            </Row>
          </Form>
        )}
      </div>
      <FloatingVideoButton isSecondFloatingButton={false} youtubeUrl={"https://youtube.com/embed/EnCQjVJVF1Y"} />

      {showModalConfig && (
        <ModalHallPointRewardsConfig
          show={showModalConfig}
          service={selectedService}
          call={load}
          closeShow={() => {
            setSelectedService(null);
            setShowModalConfig(false);
          }}
        />
      )}
    </div>
  );
}

export default HallPointRewards;